/**
 * AffDice SuperAdmin messages — the shell, and the words every screen shares.
 *
 *   `nav.*`     the menu and its two headings
 *   `shell.*`   the rail, the sign-in path and the read-only notice
 *   `common.*`  the handful of words every screen uses the same way — Save,
 *               Cancel, Edit. Shared rather than repeated per screen, so a
 *               translation of "Cancel" is decided once.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const SHELL_MESSAGES = {
  /* The menu */
  'nav.overview': { en: 'Overview', es: 'Resumen', 'pt-BR': 'Visão geral' },
  'nav.operators': { en: 'Operators', es: 'Operadores', 'pt-BR': 'Operadores' },
  'nav.brands': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'nav.affiliates': { en: 'Affiliates', es: 'Afiliados', 'pt-BR': 'Afiliados' },
  'nav.group.platform': { en: 'Platform', es: 'Plataforma', 'pt-BR': 'Plataforma' },
  'nav.users': { en: 'Users', es: 'Usuarios', 'pt-BR': 'Usuários' },
  'nav.roles': { en: 'Roles & permissions', es: 'Roles y permisos', 'pt-BR': 'Funções e permissões' },
  'nav.referenceData': { en: 'Reference data', es: 'Datos de referencia', 'pt-BR': 'Dados de referência' },
  'nav.featureFlags': { en: 'Feature flags', es: 'Funciones activables', 'pt-BR': 'Recursos ativáveis' },
  'nav.plans': { en: 'Plans', es: 'Planes', 'pt-BR': 'Planos' },
  'nav.group.integrity': { en: 'Integrity', es: 'Integridad', 'pt-BR': 'Integridade' },
  'nav.audit': { en: 'Audit chain', es: 'Cadena de auditoría', 'pt-BR': 'Cadeia de auditoria' },
  'nav.consumers': { en: 'Consumers', es: 'Consumidores', 'pt-BR': 'Consumidores' },
  'nav.accessLog': { en: 'Access log', es: 'Registro de accesos', 'pt-BR': 'Registro de acessos' },
  'nav.loginAs': { en: 'Log in as', es: 'Iniciar sesión como', 'pt-BR': 'Entrar como' },

  /* The rail and the shell */
  'shell.context': {
    en: 'Platform administration',
    es: 'Administración de la plataforma',
    'pt-BR': 'Administração da plataforma',
  },
  'shell.account': { en: 'Account', es: 'Cuenta', 'pt-BR': 'Conta' },
  'shell.staffOnly': {
    en: 'This console is for platform staff. Your role does not reach it.',
    es: 'Esta consola es para el personal de la plataforma. Tu rol no tiene acceso.',
    'pt-BR': 'Este console é para a equipe da plataforma. Sua função não tem acesso.',
  },
  'shell.readOnly': {
    en: 'Your role can view the platform console but not change anything. Actions are hidden rather than shown and refused.',
    es: 'Tu rol puede ver la consola de la plataforma, pero no cambiar nada. Las acciones se ocultan en lugar de mostrarse y rechazarse.',
    'pt-BR': 'Sua função pode ver o console da plataforma, mas não alterar nada. As ações ficam ocultas em vez de aparecer e ser recusadas.',
  },
  'shell.changePasswordTitle': {
    en: 'Change your password',
    es: 'Cambia tu contraseña',
    'pt-BR': 'Altere sua senha',
  },
  'shell.changePasswordIntro': {
    en: 'Enter your current password and choose a new one.',
    es: 'Introduce tu contraseña actual y elige una nueva.',
    'pt-BR': 'Digite sua senha atual e escolha uma nova.',
  },
  'shell.unreachable': {
    en: 'API base {base} on {origin}. Confirm api-core is on port 3001, then retry — or continue to the sign-in form.',
    es: 'Base de la API {base} en {origin}. Comprueba que api-core está en el puerto 3001 y vuelve a intentarlo, o continúa al formulario de inicio de sesión.',
    'pt-BR': 'Base da API {base} em {origin}. Confirme que o api-core está na porta 3001 e tente de novo, ou continue para o formulário de login.',
  },
  'shell.retry': { en: 'Retry', es: 'Reintentar', 'pt-BR': 'Tentar de novo' },
  'shell.continueToSignIn': {
    en: 'Continue to sign in',
    es: 'Continuar al inicio de sesión',
    'pt-BR': 'Continuar para o login',
  },

  /* Shared words */
  'common.save': { en: 'Save', es: 'Guardar', 'pt-BR': 'Salvar' },
  'common.cancel': { en: 'Cancel', es: 'Cancelar', 'pt-BR': 'Cancelar' },
  'common.edit': { en: 'Edit', es: 'Editar', 'pt-BR': 'Editar' },
  'common.delete': { en: 'Delete', es: 'Eliminar', 'pt-BR': 'Excluir' },
  'common.yes': { en: 'Yes', es: 'Sí', 'pt-BR': 'Sim' },
  'common.no': { en: 'No', es: 'No', 'pt-BR': 'Não' },
  'common.name': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'common.email': { en: 'Email', es: 'Correo electrónico', 'pt-BR': 'E-mail' },
  'common.status': { en: 'Status', es: 'Estado', 'pt-BR': 'Status' },
  'common.state': { en: 'State', es: 'Estado', 'pt-BR': 'Estado' },
  'common.reason': { en: 'Reason', es: 'Motivo', 'pt-BR': 'Motivo' },
  'common.country': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'common.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'common.operator': { en: 'Operator', es: 'Operador', 'pt-BR': 'Operador' },
  'common.brand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'common.affiliate': { en: 'Affiliate', es: 'Afiliado', 'pt-BR': 'Afiliado' },
  'common.day': { en: 'Day', es: 'Día', 'pt-BR': 'Dia' },
  'common.period': { en: 'Period', es: 'Período', 'pt-BR': 'Período' },
  'common.active': { en: 'Active', es: 'Activo', 'pt-BR': 'Ativo' },
  'common.inactive': { en: 'Inactive', es: 'Inactivo', 'pt-BR': 'Inativo' },
  'common.nobodyMatches': {
    en: 'Nobody matches that.',
    es: 'Nadie coincide con esa búsqueda.',
    'pt-BR': 'Ninguém corresponde a essa busca.',
  },
} as const satisfies Catalogue;
